import Carregando from './Carregando';
import { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Grid,
  TextField, Chip, Alert, CircularProgress, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Collapse, Checkbox, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import GroupsIcon from '@mui/icons-material/Groups';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { keyframes } from '@mui/material/styles';
import api from '../services/api';
import SecaoFormulario from './SecaoFormulario';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import ListaItensEditavel, { FAROL_OPCOES, RISCO_OPCOES, COR_BLOCO_OPCOES } from './ListaItensEditavel';
dayjs.extend(isoWeek);

const NOMES_DIA = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];
const NOMES_DIA_CURTO = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const FORMATO_DATA = 'YYYY-MM-DD';

// Semana ISO (a mesma numeração do modelo em Word: S24/2025). Semana começa na segunda-feira.
const periodoDaSemana = (base = dayjs()) => {
  const segunda = base.startOf('isoWeek');
  return {
    numero_semana: segunda.isoWeek(),
    ano: segunda.isoWeekYear(),
    data_inicio: segunda.format(FORMATO_DATA),
    data_fim: segunda.add(4, 'day').format(FORMATO_DATA),
  };
};

// Mesmo pulso do alerta de notas vencidas do Financeiro.
const pulso = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.55); }
  70% { box-shadow: 0 0 0 12px rgba(255, 255, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
`;

const EMAIL_VALIDO = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;
const quebrarEmails = (texto) => (texto || '').split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);

const FORM_VAZIO = () => ({ id: null, ...periodoDaSemana(), observacoes: '' });

const itemDaApi = (i) => ({
  _key: `i${i.id}`,
  id: i.id,
  descricao: i.descricao || '',
  responsavel: i.responsavel || '',
  data: i.data || '',
  semana_inicio: i.semana_inicio || '',
  semana_fim: i.semana_fim || '',
  farol: i.farol || '',
  recomendacao: i.recomendacao || '',
  risco: i.risco || '',
  hora_inicio: (i.hora_inicio || '').slice(0, 5),
  hora_fim: (i.hora_fim || '').slice(0, 5),
  cor: i.cor || '',
  fotos: i.fotos || [],
  foto: null,
  _fotosRemover: [],
});

const itensDoTipo = (relatorio, tipo) =>
  (relatorio.itens || [])
    .filter((i) => i.tipo === tipo)
    .sort((a, b) => a.ordem - b.ordem)
    .map(itemDaApi);

// Traduz a resposta de erro do servidor para uma frase que a pessoa entende.
const mensagemDeErro = (err) => {
  const d = err?.response?.data;
  if (!d) return 'Não foi possível salvar. Confira a conexão e tente de novo.';
  if (d.non_field_errors) {
    return 'Já existe um relatório para esse projeto nessa semana. Edite o relatório existente ou troque o número da semana.';
  }
  if (typeof d.detail === 'string') return d.detail;
  if (typeof d.error === 'string') return d.error;
  // O servidor devolve os erros dos itens como lista ou como {posição: erros}, conforme a versão.
  const errosItens = Array.isArray(d.itens)
    ? d.itens
    : (d.itens && typeof d.itens === 'object'
      ? Object.keys(d.itens).sort((a, b) => a - b).map((k) => d.itens[k])
      : null);
  if (errosItens) {
    const posicao = errosItens.findIndex((e) => e && Object.keys(e).length > 0);
    if (posicao >= 0) {
      const campo = Object.keys(errosItens[posicao])[0];
      if (campo === 'hora_inicio' || campo === 'hora_fim') {
        const msg = errosItens[posicao][campo];
        return `Programação da próxima semana: ${Array.isArray(msg) ? msg[0] : msg}`;
      }
      return `Há um item com dado inválido (${campo}). Confira as linhas preenchidas.`;
    }
  }
  if (typeof d.itens === 'string') return d.itens;
  const primeiro = Object.keys(d)[0];
  if (primeiro) {
    const valor = Array.isArray(d[primeiro]) ? d[primeiro][0] : d[primeiro];
    return `Erro ao salvar (${primeiro}): ${valor}`;
  }
  return 'Erro ao salvar o relatório.';
};

export default function RelatorioVisita({ projetoId }) {
  const [relatorios, setRelatorios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [abrirModal, setAbrirModal] = useState(false);
  const [abrirEnvio, setAbrirEnvio] = useState(false);
  const [relatorioSelecionado, setRelatorioSelecionado] = useState(null);
  const [excluindo, setExcluindo] = useState(null);
  const [expandido, setExpandido] = useState(null);
  const [emails, setEmails] = useState([]);
  const [campoEmail, setCampoEmail] = useState('');
  const [copiaParaMim, setCopiaParaMim] = useState(true);
  const [sucesso, setSucesso] = useState('');
  const [aviso, setAviso] = useState('');
  const [erro, setErro] = useState('');
  const [erroEnvio, setErroEnvio] = useState('');
  const [erroLista, setErroLista] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [apagando, setApagando] = useState(false);

  const atual = periodoDaSemana();
  const semanaAtual = atual.numero_semana;
  const anoAtual = atual.ano;

  const [form, setForm] = useState(FORM_VAZIO());
  const [statusEmEdicao, setStatusEmEdicao] = useState('rascunho');
  const [itensPlanejadas, setItensPlanejadas] = useState([]);
  const [itensExecutadas, setItensExecutadas] = useState([]);
  const [itensOportunidades, setItensOportunidades] = useState([]);
  const [itensEquipe, setItensEquipe] = useState([]);
  const [itensProgramacao, setItensProgramacao] = useState([]);

  const carregarRelatorios = () => {
    api.get(`/relatorios/?projeto=${projetoId}`).then((res) => {
      setRelatorios(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    carregarRelatorios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projetoId]);

  // ---------------------------------------------------------------- campos de cada seção
  const CAMPOS_PLANEJADAS = [
    { nome: 'descricao', label: 'Atividade', flex: 3, largura: 240, multiline: true },
    { nome: 'semana_inicio', label: 'Sem. início', largura: 90 },
    { nome: 'semana_fim', label: 'Sem. fim', largura: 90 },
    { nome: 'farol', label: 'Status', tipo: 'select', opcoes: FAROL_OPCOES, largura: 140 },
  ];
  const ITEM_VAZIO_PLANEJADA = { descricao: '', semana_inicio: '', semana_fim: '', farol: '' };

  const CAMPOS_EXECUTADAS = [
    { nome: 'descricao', label: 'Atividade', flex: 3, largura: 300, multiline: true },
    { nome: 'foto', label: 'Foto', tipo: 'foto' },
  ];
  const ITEM_VAZIO_EXECUTADA = { descricao: '', foto: null, fotos: [], _fotosRemover: [] };

  const CAMPOS_OPORTUNIDADES = [
    { nome: 'descricao', label: 'Oportunidade / Dificuldade', flex: 3, largura: 260, multiline: true },
    { nome: 'farol', label: 'Farol', tipo: 'select', opcoes: FAROL_OPCOES, largura: 130 },
    { nome: 'recomendacao', label: 'Recomendação', flex: 2, largura: 200, multiline: true },
    { nome: 'risco', label: 'Risco', tipo: 'select', opcoes: RISCO_OPCOES, largura: 110 },
  ];
  const ITEM_VAZIO_OPORTUNIDADE = { descricao: '', farol: '', recomendacao: '', risco: '' };

  const CAMPOS_EQUIPE = [
    { nome: 'descricao', label: 'Ação', flex: 3, largura: 240, multiline: true },
    { nome: 'responsavel', label: 'Responsável', largura: 140 },
    { nome: 'semana_inicio', label: 'Sem. início', largura: 90 },
    { nome: 'semana_fim', label: 'Sem. fim', largura: 90 },
    { nome: 'farol', label: 'Status', tipo: 'select', opcoes: FAROL_OPCOES, largura: 140 },
  ];
  const ITEM_VAZIO_EQUIPE = { descricao: '', responsavel: '', semana_inicio: '', semana_fim: '', farol: '' };

  // Programação: o dia é escolhido entre segunda e sexta da semana SEGUINTE ao período do relatório.
  const opcoesDiaProgramacao = () => {
    const base = form.data_fim ? dayjs(form.data_fim) : dayjs();
    const segunda = base.startOf('isoWeek').add(7, 'day');
    const opcoes = [{ value: '', label: '—' }];
    for (let i = 0; i < 5; i += 1) {
      const d = segunda.add(i, 'day');
      opcoes.push({ value: d.format(FORMATO_DATA), label: `${NOMES_DIA[i]} ${d.format('DD/MM')}` });
    }
    // dia já salvo que ficou fora dessa semana (ex.: período alterado) continua aparecendo
    itensProgramacao.forEach((it) => {
      if (it.data && !opcoes.some((o) => o.value === it.data)) {
        opcoes.push({ value: it.data, label: dayjs(it.data).format('DD/MM/YYYY') });
      }
    });
    return opcoes;
  };
  const CAMPOS_PROGRAMACAO = [
    { nome: 'data', label: 'Dia', tipo: 'select', opcoes: opcoesDiaProgramacao(), largura: 170 },
    { nome: 'hora_inicio', label: 'Início', tipo: 'hora', largura: 105 },
    { nome: 'hora_fim', label: 'Fim', tipo: 'hora', largura: 105 },
    { nome: 'cor', label: 'Cor', tipo: 'select', opcoes: COR_BLOCO_OPCOES, largura: 150 },
    { nome: 'descricao', label: 'Atividade', flex: 3, largura: 260, multiline: true },
    { nome: 'responsavel', label: 'Responsável', largura: 150 },
  ];
  const ITEM_VAZIO_PROGRAMACAO = { descricao: '', responsavel: '', data: '', hora_inicio: '', hora_fim: '', cor: '' };

  // ---------------------------------------------------------------- abrir novo / editar
  const limparListas = () => {
    setItensPlanejadas([]); setItensExecutadas([]); setItensOportunidades([]);
    setItensEquipe([]); setItensProgramacao([]);
  };

  const abrirNovo = () => {
    setErro('');
    setForm(FORM_VAZIO());
    setStatusEmEdicao('rascunho');
    limparListas();
    setAbrirModal(true);
  };

  const abrirEdicao = (r) => {
    setErro('');
    setForm({
      id: r.id,
      numero_semana: r.numero_semana,
      ano: r.ano,
      data_inicio: r.data_inicio,
      data_fim: r.data_fim,
      observacoes: r.observacoes || '',
    });
    setStatusEmEdicao(r.status);
    setItensPlanejadas(itensDoTipo(r, 'planejada'));
    setItensExecutadas(itensDoTipo(r, 'executada'));
    setItensOportunidades(itensDoTipo(r, 'oportunidade'));
    setItensEquipe(itensDoTipo(r, 'equipe'));
    setItensProgramacao(itensDoTipo(r, 'programacao'));
    setAbrirModal(true);
  };

  // Ao escolher o início da semana, o número da semana, o ano e o fim se ajustam sozinhos.
  const mudarDataInicio = (valor) => {
    const d = dayjs(valor);
    if (valor && d.isValid()) {
      const seg = d.startOf('isoWeek');
      setForm({
        ...form,
        data_inicio: valor,
        numero_semana: seg.isoWeek(),
        ano: seg.isoWeekYear(),
        data_fim: seg.add(4, 'day').format(FORMATO_DATA),
      });
    } else {
      setForm({ ...form, data_inicio: valor });
    }
  };

  // ---------------------------------------------------------------- salvar (tudo ou nada)
  const montarItens = () => {
    const grupos = [
      ['planejada', itensPlanejadas],
      ['executada', itensExecutadas],
      ['oportunidade', itensOportunidades],
      ['equipe', itensEquipe],
      ['programacao', itensProgramacao],
    ];
    const payload = [];
    const origem = [];
    grupos.forEach(([tipo, lista]) => {
      lista.forEach((it, i) => {
        if (!it.descricao?.trim()) return; // linha vazia não vai para o relatório
        payload.push({
          ...(it.id ? { id: it.id } : {}),
          tipo,
          descricao: it.descricao.trim(),
          responsavel: it.responsavel || '',
          data: it.data || null,
          semana_inicio: it.semana_inicio || '',
          semana_fim: it.semana_fim || '',
          farol: it.farol || '',
          recomendacao: it.recomendacao || '',
          risco: it.risco || '',
          hora_inicio: it.hora_inicio || null,
          hora_fim: it.hora_fim || null,
          cor: it.cor || '',
          ordem: i,
        });
        origem.push(it);
      });
    });
    return { payload, origem };
  };

  const validarForm = () => {
    if (!form.data_inicio || !form.data_fim) return 'Informe a data de início e a data de fim da semana.';
    if (dayjs(form.data_fim).isBefore(dayjs(form.data_inicio))) return 'A data de fim não pode ser antes da data de início.';
    const n = Number(form.numero_semana);
    if (!Number.isInteger(n) || n < 1 || n > 53) return 'O número da semana deve estar entre 1 e 53.';
    if (!form.ano) return 'Informe o ano.';
    // Programação: horário só vale com início E fim, e o fim vem depois do início.
    for (const it of itensProgramacao) {
      if (!it.descricao?.trim()) continue;
      const nome = `“${it.descricao.trim().slice(0, 40)}”`;
      if (!!it.hora_inicio !== !!it.hora_fim) {
        return `Programação da próxima semana, ${nome}: informe a hora de início e a de fim (ou deixe as duas em branco).`;
      }
      if (it.hora_inicio && it.hora_fim <= it.hora_inicio) {
        return `Programação da próxima semana, ${nome}: a hora de fim deve ser depois da hora de início.`;
      }
      if (it.hora_inicio && !it.data) {
        return `Programação da próxima semana, ${nome}: escolha o dia para o horário aparecer na grade.`;
      }
    }
    return '';
  };

  const handleSalvar = async () => {
    setErro('');
    const problema = validarForm();
    if (problema) { setErro(problema); return; }

    const { payload, origem } = montarItens();
    const corpo = {
      projeto: projetoId,
      numero_semana: Number(form.numero_semana),
      ano: Number(form.ano),
      data_inicio: form.data_inicio,
      data_fim: form.data_fim,
      observacoes: form.observacoes || '',
      itens: payload,
    };

    setSalvando(true);
    let resposta;
    try {
      resposta = form.id
        ? await api.put(`/relatorios/${form.id}/completo/`, corpo)
        : await api.post('/relatorios/completo/', corpo);
    } catch (err) {
      setErro(mensagemDeErro(err));
      setSalvando(false);
      return;
    }

    // O relatório e os itens já estão salvos. Agora as fotos (removidas e novas).
    const idsItens = resposta.data.itens_ids || [];
    const falhas = [];
    for (let i = 0; i < origem.length; i += 1) {
      const it = origem[i];
      for (const fotoId of it._fotosRemover || []) {
        try { await api.delete(`/fotos/${fotoId}/`); } catch { falhas.push(`remover uma foto de "${it.descricao.slice(0, 30)}"`); }
      }
      if (it.foto) {
        try {
          const formData = new FormData();
          formData.append('imagem', it.foto);
          formData.append('contexto', 'item_relatorio');
          formData.append('item_relatorio', idsItens[i]);
          await api.post('/fotos/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch {
          falhas.push(`enviar a foto de "${it.descricao.slice(0, 30)}"`);
        }
      }
    }

    setSalvando(false);
    setAbrirModal(false);
    limparListas();
    if (falhas.length) {
      setAviso(`Relatório salvo, mas não foi possível ${falhas.join('; ')}. Abra "Editar" e tente de novo.`);
      setSucesso('');
    } else {
      setAviso('');
      setSucesso(form.id
        ? (statusEmEdicao === 'enviado'
          ? 'Relatório atualizado. Ele voltou para rascunho: envie novamente ao cliente.'
          : 'Relatório atualizado!')
        : 'Relatório salvo como rascunho!');
    }
    carregarRelatorios();
  };

  // ---------------------------------------------------------------- excluir
  const handleExcluir = async () => {
    if (!excluindo) return;
    setApagando(true);
    setErroLista('');
    try {
      await api.delete(`/relatorios/${excluindo.id}/`);
      setSucesso(`Relatório da Semana ${excluindo.numero_semana}/${excluindo.ano} excluído.`);
      setExcluindo(null);
      carregarRelatorios();
    } catch (err) {
      setErroLista(err?.response?.data?.detail || 'Não foi possível excluir o relatório.');
      setExcluindo(null);
    } finally {
      setApagando(false);
    }
  };

  // ---------------------------------------------------------------- enviar e ver PDF
  const handleAbrirEnvio = (relatorio) => {
    setRelatorioSelecionado(relatorio);
    setEmails(quebrarEmails(relatorio.destinatarios));
    setCampoEmail('');
    setCopiaParaMim(true);
    setErroEnvio('');
    setAbrirEnvio(true);
  };

  // Junta o que foi digitado/colado à lista de e-mails (sem repetir). Devolve a lista nova.
  const adicionarEmails = (texto = campoEmail) => {
    const novos = quebrarEmails(texto);
    if (novos.length === 0) return emails;
    const lista = [...emails];
    novos.forEach((e) => {
      if (!lista.some((x) => x.toLowerCase() === e.toLowerCase())) lista.push(e);
    });
    setEmails(lista);
    setCampoEmail('');
    return lista;
  };

  const removerEmail = (email) => setEmails(emails.filter((e) => e !== email));

  const handleEnviar = async () => {
    const lista = adicionarEmails();
    if (lista.length === 0) {
      setErroEnvio('Adicione pelo menos um e-mail de destinatário.');
      return;
    }
    const invalidos = lista.filter((e) => !EMAIL_VALIDO.test(e));
    if (invalidos.length > 0) {
      setErroEnvio(`Corrija ou remova o e-mail destacado em vermelho: ${invalidos.join(', ')}.`);
      return;
    }
    setEnviando(true);
    setErroEnvio('');
    try {
      await api.post(`/relatorios/${relatorioSelecionado.id}/enviar/`, {
        destinatarios: lista.join(', '),
        copia_para_mim: copiaParaMim,
      });
      setSucesso(`Relatório enviado para ${lista.length} ${lista.length === 1 ? 'destinatário' : 'destinatários'}.`);
      setAbrirEnvio(false);
      carregarRelatorios();
    } catch (err) {
      setErroEnvio(err?.response?.data?.error || 'Erro ao enviar o relatório. Verifique as configurações de e-mail.');
    } finally {
      setEnviando(false);
    }
  };

  const handleVerPdf = async (relatorio) => {
    setErroLista('');
    const novaAba = window.open('', '_blank');
    try {
      const res = await api.get(`/relatorios/${relatorio.id}/pdf/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (novaAba) {
        novaAba.location.href = url;
      }
    } catch {
      if (novaAba) novaAba.close();
      setErroLista('Não foi possível gerar o PDF. Se o problema continuar, avise a equipe de sistemas.');
    }
  };

  // O alerta some quando há um relatório ENVIADO da semana ISO e do ano atuais.
  const semanaPendente = !relatorios.some(
    (r) => r.status === 'enviado' && Number(r.numero_semana) === semanaAtual && Number(r.ano) === anoAtual
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Relatórios de visita
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={abrirNovo}
          sx={{ backgroundColor: '#13375F' }}
        >
          Novo relatório
        </Button>
      </Box>

      {!loading && semanaPendente && (
        <Box
          role="alert"
          sx={{
            display: 'flex', alignItems: 'center', gap: 2, mt: 2, mb: 2.5,
            px: { xs: 2, sm: 3 }, py: 2, borderRadius: 2, color: 'white',
            background: 'linear-gradient(100deg, #B42B2A 0%, #D23C3B 100%)',
          }}
        >
          <Box
            sx={{
              width: 44, height: 44, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.18)', animation: `${pulso} 2.4s ease-out infinite`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          >
            <WarningAmberIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.35, color: 'white' }}>
              Relatório da Semana {semanaAtual} não enviado
            </Typography>
            <Typography sx={{ fontSize: 14, opacity: 0.92, color: 'white' }}>
              Clique em "Novo relatório" para preencher e enviar ao cliente.
            </Typography>
          </Box>
        </Box>
      )}

      {sucesso && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>
          {sucesso}
        </Alert>
      )}
      {aviso && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAviso('')}>
          {aviso}
        </Alert>
      )}
      {erroLista && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErroLista('')}>
          {erroLista}
        </Alert>
      )}

      {loading ? (
        <Carregando />
      ) : relatorios.length === 0 ? (
        <Box
          sx={{
            border: '2px dashed #ccc', borderRadius: 2,
            py: 6, textAlign: 'center',
          }}
        >
          <Typography color="text.secondary">
            Nenhum relatório ainda. Clique em "Novo relatório" para começar.
          </Typography>
        </Box>
      ) : (
        relatorios.map((r) => {
          const aberto = expandido === r.id;
          return (
            <Card key={r.id} sx={{ mb: 2.5, borderRadius: 3, overflow: 'hidden' }}>
              <Box
                sx={{
                  backgroundColor: r.status === 'enviado' ? '#16A34A' : '#727176',
                  px: 2.5, py: 1.3,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1,
                }}
              >
                <Box>
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 14 }}>
                    Semana {r.numero_semana} · {r.ano}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 11 }}>
                    {dayjs(r.data_inicio).format('DD/MM/YYYY')} a {dayjs(r.data_fim).format('DD/MM/YYYY')}
                    {r.status === 'enviado' && r.enviado_em && ` · Enviado em ${dayjs(r.enviado_em).format('DD/MM/YYYY [às] HH:mm')}`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.22)', color: 'white',
                      px: 1, py: 0.25, borderRadius: 1, fontSize: 11, fontWeight: 700,
                    }}
                  >
                    {r.status === 'enviado' ? 'ENVIADO' : 'RASCUNHO'}
                  </Box>
                  <IconButton size="small" onClick={() => setExpandido(aberto ? null : r.id)} sx={{ color: 'white' }} aria-label={aberto ? 'Recolher' : 'Expandir'}>
                    {aberto ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              </Box>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: aberto ? 0 : 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
                    {r.status === 'enviado' && r.pdf_congelado
                      ? 'O PDF enviado ao cliente está guardado: “Reenviar” manda exatamente o mesmo arquivo.'
                      : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={() => handleVerPdf(r)}
                      sx={{ borderColor: '#e94560', color: '#e94560' }}
                    >
                      PDF
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => abrirEdicao(r)}
                      sx={{ borderColor: '#13375F', color: '#13375F' }}
                    >
                      Editar
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setExcluindo(r)}
                    >
                      Excluir
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<SendIcon />}
                      onClick={() => handleAbrirEnvio(r)}
                      sx={{ backgroundColor: '#13375F' }}
                    >
                      {r.status === 'enviado' ? 'Reenviar' : 'Enviar'}
                    </Button>
                  </Box>
                </Box>

                <Collapse in={aberto}>
                  <Divider sx={{ my: 2, mt: aberto ? 2 : 0 }} />
                  <Box>
                    <ResumoItens titulo="Entregas planejadas para o mês" itens={r.itens} tipo="planejada" />
                    <ResumoItens titulo="Atividades executadas" itens={r.itens} tipo="executada" />
                    <ResumoItens titulo="Oportunidades e dificuldades" itens={r.itens} tipo="oportunidade" />
                    <ResumoItens titulo="Atividades a realizar pela equipe" itens={r.itens} tipo="equipe" />
                    <ResumoItens titulo="Programação da próxima semana" itens={r.itens} tipo="programacao" />
                    {r.observacoes && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Observações
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{r.observacoes}</Typography>
                      </Box>
                    )}
                    {r.destinatarios && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Enviado para
                        </Typography>
                        <Typography variant="body2">{r.destinatarios}</Typography>
                      </Box>
                    )}
                    {(!r.itens || r.itens.length === 0) && !r.observacoes && (
                      <Typography variant="body2" color="text.secondary">
                        Nenhum item preenchido neste relatório ainda.
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          );
        })
      )}

      {/* Modal novo / editar relatório */}
      <Dialog open={abrirModal} onClose={() => !salvando && setAbrirModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          {form.id ? 'Editar relatório de visita' : 'Novo relatório de visita'}
        </DialogTitle>
        <Typography variant="body2" color="text.secondary" sx={{ px: 3, mb: 1 }}>
          Semana {form.numero_semana}/{form.ano}
        </Typography>
        <DialogContent>
          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
          {form.id && statusEmEdicao === 'enviado' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Este relatório já foi enviado ao cliente. Ao salvar as alterações ele volta para
              rascunho e será preciso enviá-lo novamente.
            </Alert>
          )}

          <SecaoFormulario titulo="Período do relatório" icone={<CalendarTodayIcon sx={{ fontSize: 15, color: '#13375F' }} />} cor="#13375F">
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth label="Número da semana" type="number"
                value={form.numero_semana}
                onChange={(e) => setForm({ ...form, numero_semana: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth label="Ano" type="number"
                value={form.ano}
                onChange={(e) => setForm({ ...form, ano: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth type="date" label="Data início"
                value={form.data_inicio}
                onChange={(e) => mudarDataInicio(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth type="date" label="Data fim"
                value={form.data_fim}
                onChange={(e) => setForm({ ...form, data_fim: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
          </SecaoFormulario>

          <SecaoFormulario titulo="Entregas planejadas para o mês" icone={<AssignmentIcon sx={{ fontSize: 15, color: '#2563EB' }} />} cor="#2563EB">
            <Grid size={12}>
              <ListaItensEditavel
                itens={itensPlanejadas}
                onChange={setItensPlanejadas}
                campos={CAMPOS_PLANEJADAS}
                itemVazio={ITEM_VAZIO_PLANEJADA}
              />
            </Grid>
          </SecaoFormulario>

          <SecaoFormulario titulo="Atividades executadas na semana" icone={<PhotoLibraryIcon sx={{ fontSize: 15, color: '#16A34A' }} />} cor="#16A34A">
            <Grid size={12}>
              <ListaItensEditavel
                itens={itensExecutadas}
                onChange={setItensExecutadas}
                campos={CAMPOS_EXECUTADAS}
                itemVazio={ITEM_VAZIO_EXECUTADA}
              />
            </Grid>
          </SecaoFormulario>

          <SecaoFormulario titulo="Oportunidades e dificuldades encontradas" icone={<WarningAmberIcon sx={{ fontSize: 15, color: '#D97706' }} />} cor="#D97706">
            <Grid size={12}>
              <ListaItensEditavel
                itens={itensOportunidades}
                onChange={setItensOportunidades}
                campos={CAMPOS_OPORTUNIDADES}
                itemVazio={ITEM_VAZIO_OPORTUNIDADE}
              />
            </Grid>
          </SecaoFormulario>

          <SecaoFormulario titulo="Atividades a realizar pela equipe" icone={<GroupsIcon sx={{ fontSize: 15, color: '#7C3AED' }} />} cor="#7C3AED">
            <Grid size={12}>
              <ListaItensEditavel
                itens={itensEquipe}
                onChange={setItensEquipe}
                campos={CAMPOS_EQUIPE}
                itemVazio={ITEM_VAZIO_EQUIPE}
              />
            </Grid>
          </SecaoFormulario>

          <SecaoFormulario titulo="Programação da próxima semana" icone={<EventNoteIcon sx={{ fontSize: 15, color: '#0F766E' }} />} cor="#0F766E">
            <Grid size={12}>
              <ListaItensEditavel
                itens={itensProgramacao}
                onChange={setItensProgramacao}
                campos={CAMPOS_PROGRAMACAO}
                itemVazio={ITEM_VAZIO_PROGRAMACAO}
              />
            </Grid>
          </SecaoFormulario>

          <TextField
            fullWidth label="Observações gerais"
            multiline minRows={2}
            value={form.observacoes}
            onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Linhas sem texto na descrição não entram no relatório.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbrirModal(false)} disabled={salvando}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSalvar}
            disabled={salvando}
            startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ backgroundColor: '#13375F' }}
          >
            {salvando ? 'Salvando...' : (form.id ? 'Salvar alterações' : 'Salvar rascunho')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal envio */}
      <Dialog open={abrirEnvio} onClose={() => !enviando && setAbrirEnvio(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          {relatorioSelecionado?.status === 'enviado' ? 'Reenviar relatório ao cliente' : 'Enviar relatório ao cliente'}
        </DialogTitle>
        <Typography variant="body2" color="text.secondary" sx={{ px: 3, mb: 1 }}>
          Semana {relatorioSelecionado?.numero_semana}/{relatorioSelecionado?.ano}
          {relatorioSelecionado?.projeto_nome ? ` · ${relatorioSelecionado.projeto_nome}` : ''}
        </Typography>
        <DialogContent>
          {erroEnvio && <Alert severity="error" sx={{ mb: 2 }}>{erroEnvio}</Alert>}

          <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>Para quem enviar</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Digite o e-mail de cada pessoa do cliente que deve receber o relatório e aperte{' '}
            <strong>Enter</strong> (ou vírgula) para adicionar. Pode colocar quantos quiser.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              fullWidth size="small"
              label="E-mail do destinatário"
              placeholder="nome@cliente.com.br"
              value={campoEmail}
              onChange={(e) => {
                const v = e.target.value;
                // vírgula, ponto e vírgula ou espaço no fim = terminou de digitar um e-mail
                if (/[,;\s]$/.test(v) && v.trim()) { adicionarEmails(v); } else { setCampoEmail(v); }
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); adicionarEmails(); } }}
              onBlur={() => adicionarEmails()}
              onPaste={(e) => {
                const texto = e.clipboardData.getData('text');
                if (/[,;\s]/.test(texto.trim())) { e.preventDefault(); adicionarEmails(texto); }
              }}
            />
            <Button variant="outlined" onClick={() => adicionarEmails()} disabled={!campoEmail.trim()} sx={{ height: 40, whiteSpace: 'nowrap' }}>
              Adicionar
            </Button>
          </Box>

          <Box sx={{ mt: 1.5, minHeight: 36, display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
            {emails.length === 0 ? (
              <Typography variant="caption" color="text.secondary">Nenhum destinatário adicionado ainda.</Typography>
            ) : emails.map((email) => (
              <Chip
                key={email}
                label={email}
                onDelete={() => removerEmail(email)}
                color={EMAIL_VALIDO.test(email) ? 'default' : 'error'}
                sx={EMAIL_VALIDO.test(email) ? { backgroundColor: '#eaf0f7' } : undefined}
              />
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            sx={{ alignItems: 'flex-start', m: 0 }}
            control={
              <Checkbox
                checked={copiaParaMim && !!relatorioSelecionado?.copia_email}
                disabled={!relatorioSelecionado?.copia_email}
                onChange={(e) => setCopiaParaMim(e.target.checked)}
                sx={{ pt: 0, pl: 0 }}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Enviar uma cópia para mim
                  {relatorioSelecionado?.copia_email ? ` (${relatorioSelecionado.copia_email})` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {relatorioSelecionado?.copia_email
                    ? 'Fica só com você, como registro do que o cliente recebeu. Os destinatários não veem essa cópia.'
                    : 'Seu usuário não tem e-mail cadastrado, por isso não dá para receber a cópia.'}
                </Typography>
              </Box>
            }
          />

          <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, backgroundColor: '#f4f6f9', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <PictureAsPdfIcon sx={{ color: '#e94560' }} />
            <Typography variant="body2" sx={{ flex: 1, minWidth: 200 }}>
              {relatorioSelecionado?.status === 'enviado' && relatorioSelecionado?.pdf_congelado
                ? 'Será reenviado exatamente o mesmo PDF que o cliente recebeu da primeira vez.'
                : 'O PDF do relatório será gerado agora e seguirá como anexo.'}
            </Typography>
            <Button size="small" onClick={() => handleVerPdf(relatorioSelecionado)}>Ver o PDF</Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbrirEnvio(false)} disabled={enviando}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            onClick={handleEnviar}
            disabled={enviando}
            sx={{ backgroundColor: '#13375F' }}
          >
            {enviando
              ? 'Enviando...'
              : (emails.length > 0 ? `Enviar para ${emails.length} ${emails.length === 1 ? 'pessoa' : 'pessoas'}` : 'Enviar relatório')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmação de exclusão */}
      <Dialog open={!!excluindo} onClose={() => !apagando && setExcluindo(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir relatório?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            O relatório da <strong>Semana {excluindo?.numero_semana}/{excluindo?.ano}</strong> e todos os
            itens dele serão apagados. Essa ação não pode ser desfeita.
          </Typography>
          {excluindo?.status === 'enviado' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Este relatório já foi enviado ao cliente. Excluí-lo aqui não desfaz o e-mail enviado.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExcluindo(null)} disabled={apagando}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleExcluir} disabled={apagando}>
            {apagando ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const CORES_FAROL_CHIP = {
  azul:     '#2563EB',
  amarelo:  '#D97706',
  verde:    '#16A34A',
  vermelho: '#DC2626',
};
const CORES_RISCO_CHIP = { baixo: 'success', medio: 'warning', alto: 'error' };

function ResumoItens({ titulo, itens, tipo }) {
  const filtrados = (itens || [])
    .filter((i) => i.tipo === tipo)
    .sort((a, b) => (tipo === 'programacao'
      ? String(a.data || '9999').localeCompare(String(b.data || '9999')) || a.ordem - b.ordem
      : a.ordem - b.ordem));
  if (filtrados.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
        {titulo}
      </Typography>
      {filtrados.map((item) => {
        const meta = [];
        if (item.data) meta.push(`${NOMES_DIA_CURTO[dayjs(item.data).day()]} ${dayjs(item.data).format('DD/MM')}`);
        if (item.hora_inicio && item.hora_fim) meta.push(`${item.hora_inicio.slice(0, 5)}–${item.hora_fim.slice(0, 5)}`);
        if (item.responsavel) meta.push(item.responsavel);
        if (item.semana_inicio || item.semana_fim) {
          meta.push(`${item.semana_inicio || '—'} a ${item.semana_fim || '—'}`);
        }
        return (
          <Box
            key={item.id}
            sx={{ py: 0.75, borderBottom: '1px solid #f0f0f0', '&:last-child': { borderBottom: 'none' } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ flex: 1, minWidth: 150, whiteSpace: 'pre-line' }}>
                {item.descricao}
              </Typography>
              {meta.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {meta.join(' · ')}
                </Typography>
              )}
              {item.farol && (
                <Chip
                  label={item.farol_display}
                  size="small"
                  sx={{ backgroundColor: CORES_FAROL_CHIP[item.farol] || '#999', color: 'white', fontWeight: 600 }}
                />
              )}
              {item.risco && (
                <Chip label={item.risco_display} color={CORES_RISCO_CHIP[item.risco] || 'default'} size="small" />
              )}
            </Box>
            {item.recomendacao && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                Recomendação: {item.recomendacao}
              </Typography>
            )}
            {item.fotos && item.fotos.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                {item.fotos.map((foto) => (
                  <Box
                    key={foto.id}
                    component="img"
                    src={foto.imagem_url}
                    alt={foto.descricao || ''}
                    sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }}
                    onClick={() => window.open(foto.imagem_url, '_blank')}
                  />
                ))}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
